import { consoleEvents } from '@pb/utils'
import { createReducer } from 'redux-act'

import {
  StyleboardState,
  StyleboardType,
  Notification,
  FetchStyleboardResponse,
  NotificationType,
  StyleboardSuccessLocalState,
  Styleboard,
  FetchSimilarItemsResponse,
} from '../../types'
import actions from './actions'
import { getCorrectNotification } from './helpers'
import initalState from './initialState'

const filterUniqueValues = <T>(array: Array<T>, property: string): Array<T> => {
  return Array.from(new Map(array.map((item) => [item[property], item])).values())
}

const resetStyleboardState = (state): StyleboardState => ({
  ...state,
  styleboard: undefined,
  hasDeletedStyleboardSucceded: false,
})

const fetchMoreDataStart = (state): StyleboardState => ({
  ...state,
  isStyleboardLoading: true,
})

const fetchMoreDataSuccess = (state, payload): StyleboardState => {
  return {
    ...state,
    isStyleboardLoading: false,
    styleboard: {
      ...state.styleboard,
      ...(payload.items && {
        items: filterUniqueValues<Styleboard>([...state.styleboard.items, ...payload.items], 'id'),
        _links: payload._links,
      }),
    },
  }
}

const fetchMoreDataFail = (state, error): StyleboardState => {
  consoleEvents.error('Failed to fetch more', error)

  return {
    ...state,
    isStyleboardLoading: false,
    hasStyleboardLoadingFailed: true,
  }
}

const fetchStyleboardStart = (state): StyleboardState => ({
  ...state,
  isStyleboardLoading: true,
  hasAddItemToboardSucceded: false,
})

const fetchStyleboardSuccess = (state, payload: FetchStyleboardResponse): StyleboardState => {
  return {
    ...state,
    isStyleboardLoading: false,
    styleboard: {
      ...(payload.styleboard ?? state.styleboard),
      ...(payload.items && {
        items: filterUniqueValues<Styleboard>([...state.styleboard.items, ...payload.items], 'id'),
      }),
    },
  }
}

const fetchStyleboardFail = (state, error): StyleboardState => {
  consoleEvents.error('Failed to fetch styleboard', error)

  return {
    ...state,
    isStyleboardLoading: false,
    hasStyleboardLoadingFailed: true,
  }
}

const fetchSimilarItemsStart = (state): StyleboardState => ({
  ...state,
  isSimilarItemsLoading: true,
})

const fetchSimilarItemsSuccess = (state, payload: FetchSimilarItemsResponse): StyleboardState => ({
  ...state,
  isSimilarItemsLoading: false,
  hasSimilarItemsLoaded: true,
  styleboard: {
    ...state.styleboard,
    similarItems: {
      ...payload,
      articles: payload.articles.map((article) => ({ ...article, isAddedToBoard: false })),
    },
  },
})

const fetchSimilarItemsFail = (state, error): StyleboardState => {
  consoleEvents.error('Failed to fetch similar items', error)
  return {
    ...state,
    isSimilarItemsLoading: false,
    hasSimilarItemsLoaded: true,
    hasSimilarItemsLoadingFailed: true,
  }
}

const resetSimilarItemsState = (state): StyleboardState => ({
  ...state,
  styleboard: {
    ...(state.styleboard ? state.styleboard : undefined),
    similarItems: undefined,
  },
  isSimilarItemsLoading: false,
  hasSimilarItemsLoaded: false,
  hasSimilarItemsLoadingFailed: false,
})

const fetchStyleboardsStart = (state): StyleboardState => ({
  ...state,
  isStyleboardsLoading: true,
})

const fetchStyleboardsSuccess = (state, payload): StyleboardState => {
  return {
    ...state,
    myStyleboards: {
      ...payload,
      styleboards: filterUniqueValues<Styleboard>(
        [
          ...(payload.mergeDeep && state.myStyleboards ? state.myStyleboards.styleboards : []),
          ...payload.styleboards,
        ],
        'id',
      ),
    },
    isStyleboardsLoading: false,
  }
}

const fetchStyleboardsFail = (state, error): StyleboardState => {
  consoleEvents.error('Failed to fetch styleboards', error)

  return {
    ...state,
    isStyleboardsLoading: false,
    hasStyleboardsLoadingFailed: true,
  }
}

const fetchFollowStyleboardsStart = (state): StyleboardState => ({
  ...state,
  hasFollowStyleboardsLoaded: false,
})

const fetchFollowStyleboardsSuccess = (state, payload): StyleboardState => {
  return {
    ...state,
    followStyleboards: {
      ...payload,
      styleboards: filterUniqueValues<Styleboard>(
        [
          ...(payload.mergeDeep && state.followStyleboards
            ? state.followStyleboards.styleboards
            : []),
          ...payload.styleboards,
        ],
        'id',
      ),
    },
    hasFollowStyleboardsLoaded: true,
  }
}

const fetchFollowStyleboardsFail = (state, error): StyleboardState => {
  consoleEvents.error('Failed to fetch styleboards', error)

  return {
    ...state,
    hasFollowStyleboardsLoaded: true,
    hasFollowStyleboardsLoadedFailed: true,
  }
}

const fetchPopularStyleboardsStart = (state): StyleboardState => ({
  ...state,
  hasPopularStyleboardsLoaded: false,
})

const fetchPopularStyleboardsSuccess = (state, payload): StyleboardState => {
  return {
    ...state,
    popularStyleboards: payload,
    hasPopularStyleboardsLoaded: true,
  }
}

const fetchPopularStyleboardFail = (state, error): StyleboardState => {
  consoleEvents.error('Failed to fetch popular styleboards', error)
  return {
    ...state,
    hasPopularStyleboardsLoaded: true,
    hasPopularStyleboardsLoadedFailed: true,
  }
}

const followStyleboardStart = (state): StyleboardState => ({
  ...state,
  isFollowingStyleboardLoading: true,
  hasFollowingStyleboardLoadingFailed: false,
})

const followStyleboardSuccess = (state): StyleboardState => ({
  ...state,
  styleboard: state.styleboard && {
    ...state.styleboard,
    type: StyleboardType.FOLLOWING,
    followers: state.styleboard.followers + 1,
  },
  isFollowingStyleboardLoading: false,
})

const followStyleboardFail = (state, error): StyleboardState => {
  consoleEvents.error('Failed to follow styleboard', error)

  return {
    ...state,
    isFollowingStyleboardLoading: false,
    hasFollowingStyleboardLoadingFailed: true,
  }
}

const unFollowStyleboardStart = (state): StyleboardState => ({
  ...state,
  isFollowingStyleboardLoading: true,
})

const unFollowStyleboardSuccess = (state): StyleboardState => {
  return {
    ...state,
    styleboard: {
      ...state.styleboard,
      type: StyleboardType.NOTFOLLOWING,
      followers: state.styleboard.followers > 1 ? state.styleboard.followers - 1 : 0,
    },
    isFollowingStyleboardLoading: false,
  }
}

const unFollowStyleboardFail = (state, error): StyleboardState => {
  consoleEvents.error('Failed to unFollow styleboard', error)

  return {
    ...state,
    isFollowingStyleboardLoading: false,
    hasFollowingStyleboardLoadingFailed: true,
  }
}

const removeNotification = (state, notification: Notification): StyleboardState => ({
  ...state,
  notifications: [...state.notifications.filter((item) => item.id !== notification.id)],
})

const removeNotificationByType = (state, type: NotificationType): StyleboardState => {
  const notification = getCorrectNotification(type)
  return {
    ...state,
    notifications: [...state.notifications.filter((item) => item.id !== notification.id)],
  }
}

const addNotification = (state, type: NotificationType): StyleboardState => {
  const notifications = Array.from(new Set([...state.notifications, getCorrectNotification(type)]))

  return {
    ...state,
    notifications,
  }
}

const putAuthorSuccess = (state, userName): StyleboardState => {
  return {
    ...state,
    styleboard: {
      ...state.styleboard,
      author: {
        ...state.styleboard.author,
        userName,
      },
    },
  }
}

const putItemToStyleboardStart = (state): StyleboardState => {
  return {
    ...state,
    hasAddItemToboardFailed: false,
    hasAddItemToboardSucceded: false,
  }
}

const putItemToStyleboardSuccess = (state, itemCode): StyleboardState => {
  return {
    ...state,
    hasAddItemToboardSucceded: true,
    styleboard: {
      ...state.styleboard,
      similarItems: {
        ...state.styleboard.similarItems,
        // modify the added item
        articles: state.styleboard.similarItems.articles.map((article) => {
          if (article.code === itemCode) return { ...article, isAddedToBoard: true }
          return article
        }),
      },
    },
  }
}

const putItemToStyleboardFailed = (state): StyleboardState => {
  return {
    ...state,
    hasAddItemToboardFailed: true,
    hasAddItemToboardSucceded: false,
  }
}

const putStyleboardSuccess = (state, payload: StyleboardSuccessLocalState): StyleboardState => {
  return {
    ...state,
    styleboard: {
      ...state.styleboard,
      title: payload.title,
      about: payload.about,
      status: payload.status,
    },
  }
}

const addImageStart = (state): StyleboardState => {
  return {
    ...state,
    hasUploadingImageSucceded: false,
    isUploadingImageLoading: true,
    hasUploadingImageFailed: false,
  }
}

const addImageFail = (state): StyleboardState => {
  return {
    ...state,
    hasUploadingImageSucceded: false,
    isUploadingImageLoading: false,
    hasUploadingImageFailed: true,
  }
}

const addImageSuccess = (state): StyleboardState => {
  return {
    ...state,
    hasUploadingImageSucceded: true,
    isUploadingImageLoading: false,
    hasUploadingImageFailed: false,
  }
}

const resetImageUploadStatus = (state): StyleboardState => {
  return {
    ...state,
    hasUploadingImageSucceded: false,
    isUploadingImageLoading: false,
    hasUploadingImageFailed: false,
  }
}

const individualImageChange = (state, individualItemId: string): StyleboardState => {
  return {
    ...state,
    individualItemId,
  }
}

const deleteItemFromStyleboardStart = (state): StyleboardState => {
  return {
    ...state,
    isDeletionItemLoading: true,
    hasDeletedItemSucceded: false,
    hasDeletedItemFailed: false,
  }
}
const deleteItemFromStyleboardSuccess = (state, itemId): StyleboardState => {
  return {
    ...state,
    isDeletionItemLoading: false,
    hasDeletedItemSucceded: true,
    hasDeletedItemFailed: false,
    styleboard: {
      ...state.styleboard,
      items: state.styleboard.items.filter((item) => item.id !== itemId),
    },
  }
}
const deleteItemFromStyleboardFailed = (state): StyleboardState => {
  return {
    ...state,
    isDeletionItemLoading: false,
    hasDeletedItemSucceded: false,
    hasDeletedItemFailed: true,
  }
}

const deleteStyleboardStart = (state): StyleboardState => {
  return {
    ...state,
    isDeletingStyleboardLoading: true,
    hasDeletedStyleboardSucceded: false,
    hasDeletedStyleboardFailed: false,
  }
}

const deleteStyleboardSuccess = (state, styleboardId): StyleboardState => {
  return {
    ...state,
    ...(state.myStyleboards && {
      myStyleboards: {
        ...state.myStyleboards,
        styleboards: state.myStyleboards.styleboards.filter((item) => item.id !== styleboardId),
      },
    }),
    isDeletingStyleboardLoading: false,
    hasDeletedStyleboardSucceded: true,
    hasDeletedStyleboardFailed: false,
  }
}

const deleteStyleboardFailed = (state): StyleboardState => {
  return {
    ...state,
    isDeletingStyleboardLoading: false,
    hasDeletedStyleboardSucceded: false,
    hasDeletedStyleboardFailed: true,
  }
}

const changeBackgroundImageSuccess = (state, newImageUrl): StyleboardState => {
  return {
    ...state,
    styleboard: {
      ...state.styleboard,
      image: newImageUrl,
    },
  }
}

const fetchPublicStyleboardsStart = (state): StyleboardState => ({
  ...state,
  isPublicStyleboardsLoading: true,
  hasPublicStyleboardsLoadingFailed: false,
})

const fetchPublicStyleboardsSuccess = (state, payload): StyleboardState => {
  return {
    ...state,
    publicStyleboards: {
      ...payload,
      styleboards: filterUniqueValues<Styleboard>(
        [
          ...(payload.mergeDeep && state.publicStyleboards
            ? state.publicStyleboards.styleboards
            : []),
          ...payload.styleboards,
        ],
        'id',
      ),
    },
    isPublicStyleboardsLoading: false,
    hasPublicStyleboardsLoadingFailed: false,
  }
}

const fetchPublicStyleboardsFail = (state, error): StyleboardState => {
  consoleEvents.error('Failed to fetch public styleboards', error)

  return {
    ...state,
    isPublicStyleboardsLoading: false,
    hasPublicStyleboardsLoadingFailed: true,
  }
}

const reducer = createReducer((on) => {
  on(actions.fetchMoreDataStart, fetchMoreDataStart)
  on(actions.fetchMoreDataSuccess, fetchMoreDataSuccess)
  on(actions.fetchMoreDataFail, fetchMoreDataFail)
  on(actions.fetchStyleboardStart, fetchStyleboardStart)
  on(actions.fetchStyleboardSuccess, fetchStyleboardSuccess)
  on(actions.fetchStyleboardFail, fetchStyleboardFail)
  on(actions.fetchSimilarItemsStart, fetchSimilarItemsStart)
  on(actions.fetchSimilarItemsSuccess, fetchSimilarItemsSuccess)
  on(actions.fetchSimilarItemsFail, fetchSimilarItemsFail)
  on(actions.resetSimilarItemsState, resetSimilarItemsState)
  on(actions.resetStyleboardState, resetStyleboardState)
  on(actions.fetchStyleboardsStart, fetchStyleboardsStart)
  on(actions.fetchStyleboardsSuccess, fetchStyleboardsSuccess)
  on(actions.fetchStyleboardsFail, fetchStyleboardsFail)
  on(actions.fetchFollowStyleboardsStart, fetchFollowStyleboardsStart)
  on(actions.fetchFollowStyleboardsSuccess, fetchFollowStyleboardsSuccess)
  on(actions.fetchFollowStyleboardsFail, fetchFollowStyleboardsFail)
  on(actions.fetchPopularStyleboardsSuccess, fetchPopularStyleboardsSuccess)
  on(actions.fetchPopularStyleboardsStart, fetchPopularStyleboardsStart)
  on(actions.fetchPopularStyleboardsFail, fetchPopularStyleboardFail)
  on(actions.followStyleboardStart, followStyleboardStart)
  on(actions.followStyleboardSuccess, followStyleboardSuccess)
  on(actions.followStyleboardFail, followStyleboardFail)
  on(actions.unfollowStyleboardStart, unFollowStyleboardStart)
  on(actions.unfollowStyleboardSuccess, unFollowStyleboardSuccess)
  on(actions.unfollowStyleboardFail, unFollowStyleboardFail)
  on(actions.removeNotification, removeNotification)
  on(actions.removeNotificationByType, removeNotificationByType)
  on(actions.addNotification, addNotification)
  on(actions.putAuthorSuccess, putAuthorSuccess)
  on(actions.putItemToStyleboardStart, putItemToStyleboardStart)
  on(actions.putItemToStyleboardSuccess, putItemToStyleboardSuccess)
  on(actions.putItemToStyleboardFailed, putItemToStyleboardFailed)
  on(actions.putStyleboardSuccess, putStyleboardSuccess)
  on(actions.addImageStart, addImageStart)
  on(actions.addImageFail, addImageFail)
  on(actions.addImageSuccess, addImageSuccess)
  on(actions.resetImageUploadStatus, resetImageUploadStatus)
  on(actions.individualImageChange, individualImageChange)
  on(actions.deleteItemFromStyleboardStart, deleteItemFromStyleboardStart)
  on(actions.deleteItemFromStyleboardSuccess, deleteItemFromStyleboardSuccess)
  on(actions.deleteItemFromStyleboardFailed, deleteItemFromStyleboardFailed)
  on(actions.deleteStyleboardStart, deleteStyleboardStart)
  on(actions.deleteStyleboardSuccess, deleteStyleboardSuccess)
  on(actions.deleteStyleboardFailed, deleteStyleboardFailed)
  on(actions.changeBackgroundImageSuccess, changeBackgroundImageSuccess)
  on(actions.fetchPublicStyleboardsStart, fetchPublicStyleboardsStart)
  on(actions.fetchPublicStyleboardsSuccess, fetchPublicStyleboardsSuccess)
  on(actions.fetchPublicStyleboardsFail, fetchPublicStyleboardsFail)
}, initalState)

export default reducer
